import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/ui/cn";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Read out when the dialog opens; also shown unless `title` is given. */
  label: string;
  title?: ReactNode;
  /** Controls placed in the header, between the title and the close button. */
  actions?: ReactNode;
  children: ReactNode;
  /** `sheet` fills most of the viewport, for looking at a drawing. */
  size?: "md" | "sheet";
}

/**
 * A modal. Radix keeps focus inside while it is open, closes on Escape and on a click
 * outside, and hands focus back to whatever opened it.
 */
export function Dialog({
  open,
  onOpenChange,
  label,
  title,
  actions,
  children,
  size = "md",
}: Props) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className={cn(
            "fixed top-1/2 left-1/2 z-50 flex -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-surface shadow-pop focus:outline-none",
            size === "sheet"
              ? "h-[90dvh] w-[94vw] max-w-[1600px]"
              : "max-h-[85dvh] w-[min(560px,92vw)]",
          )}
        >
          <header className="flex items-center gap-3 border-b border-line px-4 py-2.5">
            <RadixDialog.Title className="min-w-0 flex-1 truncate text-[15px] font-semibold text-ink">
              {title ?? label}
            </RadixDialog.Title>
            {actions}
            <RadixDialog.Close
              aria-label="Close"
              className="grid size-8 place-items-center rounded-md text-muted hover:bg-sunken hover:text-ink"
            >
              <X aria-hidden="true" className="size-4" />
            </RadixDialog.Close>
          </header>
          <div className="min-h-0 flex-1">{children}</div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
